import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { authLogin, authMe, setAuthToken, getAuthToken } from "@/lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  // Bootstrap session from token on mount
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const token = getAuthToken();
      if (!token) {
        setLoading(false);
        return;
      }
      try {
        const me = await authMe();
        if (!cancelled) setUser(me);
      } catch (_) {
        setAuthToken(null);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const login = useCallback(async (email, password) => {
    const { token, user } = await authLogin(email, password);
    setAuthToken(token);
    setUser(user);
    return user;
  }, []);

  const logout = useCallback(() => {
    setAuthToken(null);
    setUser(null);
    window.location.href = "/login";
  }, []);

  const isNarwhal = user?.role === "narwhal";
  const isClient = user?.role === "client";
  const canAccessClient = (clientId) =>
    !user ? false : (isNarwhal || (user.assigned_client_ids || []).includes(clientId));

  return (
    <AuthContext.Provider value={{ user, loading, login, logout, isNarwhal, isClient, canAccessClient }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used inside <AuthProvider>");
  return ctx;
}
